import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n";
import { useStoreLinks } from "@/lib/stores";
import { trackStoreClick } from "@/lib/analytics";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { t, getLocalizedPath, switchLanguagePath, language } = useLanguage();

  // Google Play on Android devices, App Store everywhere else.
  const { primaryUrl: appStoreUrl, primaryStore } = useStoreLinks(language);

  const navLinks = [
    { href: getLocalizedPath("/"), label: t("header.home") },
    { href: getLocalizedPath("/privacy"), label: t("header.privacy") },
    { href: getLocalizedPath("/terms"), label: t("header.terms") },
    { href: getLocalizedPath("/support"), label: t("header.support") },
    { href: getLocalizedPath("/blog"), label: t("header.blog") },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 10);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Trigger mount animation
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Animate mobile menu height
  useEffect(() => {
    const el = mobileMenuRef.current;
    if (!el) return;

    if (mobileMenuOpen) {
      el.style.height = "0px";
      el.style.opacity = "0";
      el.style.display = "block";
      requestAnimationFrame(() => {
        el.style.height = `${el.scrollHeight}px`;
        el.style.opacity = "1";
      });
    } else {
      el.style.height = `${el.scrollHeight}px`;
      requestAnimationFrame(() => {
        el.style.height = "0px";
        el.style.opacity = "0";
      });
      const onEnd = () => {
        if (!mobileMenuOpen) {
          el.style.display = "none";
        }
      };
      el.addEventListener("transitionend", onEnd, { once: true });
      return () => el.removeEventListener("transitionend", onEnd);
    }
  }, [mobileMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <nav
          className={cn(
            "pointer-events-auto relative rounded-2xl border transition-all duration-500 ease-out",
            "bg-background/60 backdrop-blur-xl",
            mounted
              ? "translate-y-0 opacity-100"
              : "-translate-y-5 opacity-0",
            scrolled
              ? "border-primary/20 shadow-[0_0_20px_-4px_hsl(160_72%_42%/0.15),0_8px_32px_-8px_hsl(220_20%_4%/0.5)]"
              : "border-border/50 shadow-[0_4px_24px_-4px_hsl(220_20%_4%/0.3)]",
            "hover:border-primary/15 hover:shadow-[0_0_24px_-4px_hsl(160_72%_42%/0.1),0_8px_32px_-8px_hsl(220_20%_4%/0.5)]"
          )}
        >
          {/* Subtle top edge highlight */}
          <div className="absolute inset-x-0 top-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />

          <div className="flex h-14 items-center justify-between px-4 sm:px-6">
            {/* Logo */}
            <Link to={getLocalizedPath("/")} className="flex items-center group">
              <img
                src="/logo-test-myreceipt-17.png"
                alt="ReFound"
                className="h-6 opacity-90 transition-opacity duration-300 group-hover:opacity-100"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    "relative px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 rounded-lg",
                    isActive(link.href)
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {isActive(link.href) ? (
                    <span className="absolute inset-0 rounded-lg bg-muted/60 animate-fade-in" />
                  ) : null}
                  <span className="relative z-10">{link.label}</span>
                </Link>
              ))}
            </div>

            {/* Language Switcher + Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to={switchLanguagePath()}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                title={language === "en" ? "Passer au francais" : "Switch to English"}
              >
                <Globe className="h-4 w-4" />
                <span>{language === "en" ? "FR" : "EN"}</span>
              </Link>
              <Button
                asChild
                size="sm"
                className={cn(
                  "relative overflow-hidden rounded-lg",
                  "bg-primary text-primary-foreground font-semibold",
                  "hover:bg-primary/90",
                  "shadow-[0_0_12px_-2px_hsl(160_72%_42%/0.4)]",
                  "hover:shadow-[0_0_20px_-2px_hsl(160_72%_42%/0.5)]",
                  "transition-shadow duration-300"
                )}
              >
                <a
                  href={appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackStoreClick(primaryStore, "nav", language)}
                >
                  {/* Shimmer sweep overlay */}
                  <span className="absolute inset-0 pointer-events-none animate-shimmer-sweep" />
                  <span className="relative">{t("header.download")}</span>
                </a>
              </Button>
            </div>

            {/* Mobile menu toggle */}
            <button
              type="button"
              className="md:hidden relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              <span
                className={cn(
                  "absolute transition-all duration-200",
                  mobileMenuOpen
                    ? "rotate-0 opacity-100"
                    : "rotate-90 opacity-0"
                )}
              >
                <X className="h-5 w-5" />
              </span>
              <span
                className={cn(
                  "transition-all duration-200",
                  mobileMenuOpen
                    ? "-rotate-90 opacity-0"
                    : "rotate-0 opacity-100"
                )}
              >
                <Menu className="h-5 w-5" />
              </span>
            </button>
          </div>

          {/* Mobile Navigation */}
          <div
            ref={mobileMenuRef}
            className="md:hidden overflow-hidden transition-all duration-350 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ height: 0, opacity: 0, display: "none" }}
          >
            <div className="border-t border-border/50 mx-4" />
            <div className="px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link, index) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center px-3 py-2.5 rounded-lg text-base font-medium transition-all min-h-[44px]",
                    "translate-x-0 opacity-100",
                    isActive(link.href)
                      ? "text-foreground bg-muted/60"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  )}
                  style={{
                    transitionDelay: mobileMenuOpen
                      ? `${index * 50}ms`
                      : "0ms",
                  }}
                >
                  {link.label}
                </Link>
              ))}
              {/* Language switcher in mobile menu */}
              <Link
                to={switchLanguagePath()}
                className={cn(
                  "flex items-center gap-2 px-3 py-2.5 rounded-lg text-base font-medium transition-all min-h-[44px]",
                  "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                )}
                style={{
                  transitionDelay: mobileMenuOpen
                    ? `${navLinks.length * 50}ms`
                    : "0ms",
                }}
              >
                <Globe className="h-4 w-4" />
                {language === "en" ? "Francais" : "English"}
              </Link>
              <div
                className="pt-2"
                style={{
                  transitionDelay: mobileMenuOpen
                    ? `${(navLinks.length + 1) * 50}ms`
                    : "0ms",
                }}
              >
                <Button
                  asChild
                  className={cn(
                    "w-full relative overflow-hidden rounded-lg",
                    "bg-primary text-primary-foreground font-semibold",
                    "shadow-[0_0_12px_-2px_hsl(160_72%_42%/0.4)]"
                  )}
                >
                  <a
                    href={appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackStoreClick(primaryStore, "nav_mobile", language)}
                  >
                    <span className="absolute inset-0 pointer-events-none animate-shimmer-sweep" />
                    <span className="relative">{t("header.download")}</span>
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}
